import React, { useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { User as OidcUser } from 'oidc-client';
import {
  ActionStatusInfo,
  ContactRoleId,
  createNotificationService,
  createResourceApi,
  ErrorBoundary,
  GenericError,
  getNotificationService,
  useSettings,
  getResourceApi,
  PermissionsProvider,
  AppearanceProvider,
  AppearanceContext,
} from '@erecruit/modules';
import { Intl } from '@erecruit/core';
import {
  FrameLayout,
  NotificationsContainer,
  styled,
  Spinner,
} from '@erecruit/ui-kit';
import { onApiErrorHandler } from '@erecruit/modules';

import EditModeContextProvider from './components/EditModeContextProvider';
import {
  useNotifications,
  notificationActions,
} from '../components/Notifications';

import Menu from './components/Menu';
import Header from './components/Header';
import Content from './components/Content';
import ErrorContainer from './components/ErrorContainer';
import GlobalDispatchWatcher from './GlobalDispatchWatcher';

import {
  getIsLoadingUser,
  getOidcUser,
  getUserName,
  isAutheticated,
  getLoggedUserId,
  getLoggedInMode,
} from './selectors';

import { LoadingPage } from '../pages';
import { RootState } from '../redux/reducers';
import getUserManager from '../security/oidcUserManager';
import { FooterContext, FooterContainer } from './components/FooterPortal';
import { InsightContext, InsightContainer } from './components/InsightPortal';
import { UnappropriateRoleInfo } from './components/UnsupportedRoleInfo';
import { useAppConfig } from '../appConfig';

const { injectIntl } = Intl;

interface AppProps {
  isAutheticated: boolean;
  isLoadingUser: boolean;
  oidcUser?: OidcUser;
  userName: string;
  loggedInMode: number;
  loggedInId: number;
  push: typeof notificationActions['push'];
  getUnreadCount: typeof notificationActions['getUnreadCount'];
}

export const AppContainer = styled.div({
  height: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export default connect(
  (state: RootState) => ({
    isAutheticated: process.env.NO_OIDC ? true : isAutheticated(state),
    isLoadingUser: getIsLoadingUser(state),
    oidcUser: getOidcUser(state),
    userName: getUserName(state),
    loggedInId: getLoggedUserId(state),
    loggedInMode: getLoggedInMode(state),
  }),
  {
    push: notificationActions.push,
    getUnreadCount: notificationActions.getUnreadCount,
  },
)(
  injectIntl(
    ({
      history,
      isLoadingUser,
      isAutheticated,
      location,
      oidcUser,
      userName,
      intl,
      loggedInId,
      loggedInMode,
      push,
      getUnreadCount,
    }: AppProps & RouteComponentProps & Intl.InjectedIntlProps) => {
      const insightRef = useRef(null);
      const footerRef = useRef(null);
      const firebaseInitialized = useRef(false);
      const userManager = getUserManager();
      const isLoadingSettings = useSettings(loggedInId);
      const appConfig = useAppConfig();

      if (!isAutheticated && !isLoadingUser) {
        userManager.signinRedirect();
      }

      useEffect(() => {
        const accessToken = oidcUser?.access_token;

        if (accessToken) {
          try {
            // Reset existing tokens in all relevant service wrappers
            getResourceApi().setAccessToken(accessToken);
          } catch (error) {
            console.warn('Unable to set access token!');
          }
        }
      }, [oidcUser]);

      useNotifications(push, getUnreadCount, intl);

      if (!isLoadingUser && oidcUser && appConfig) {
        createResourceApi(
          appConfig.REACT_APP_API_URL,
          oidcUser.access_token || '',
          onApiErrorHandler,
        );

        createNotificationService();

        if (!firebaseInitialized.current) {
          getNotificationService().initialize(
            appConfig.REACT_APP_FIREBASE_CONFIG,
            () => getUnreadCount(),
          );
          firebaseInitialized.current = true;
        }
      }

      if (!isLoadingUser && isAutheticated && loggedInMode !== ContactRoleId) {
        return <UnappropriateRoleInfo idToken={oidcUser?.id_token} />;
      }

      if (!isAutheticated || isLoadingUser) {
        return <LoadingPage />;
      }

      const handleLogout = (): void => {
        userManager.removeUser();
        // eslint-disable-next-line @typescript-eslint/camelcase
        userManager.signoutRedirect({ id_token_hint: oidcUser?.id_token });

        if ('serviceWorker' in navigator) {
          navigator.serviceWorker
            .getRegistrations()
            .then(registrations =>
              registrations.forEach(registration => registration.unregister()),
            );
        }
      };

      const goToSettings = () => history.push('/settings');

      return (
        <PermissionsProvider>
          <AppearanceProvider>
            <FooterContext.Provider value={footerRef}>
              <InsightContext.Provider value={insightRef}>
                <ErrorBoundary errorComponent={GenericError}>
                  <EditModeContextProvider>
                    <AppearanceContext.Consumer>
                      {({ appearance }) => (
                        <>
                          <AppContainer>
                            {isLoadingSettings || !appearance ? (
                              <Spinner
                                tip={intl.formatMessage({
                                  id: messageIds.vms.settings.initializing,
                                })}
                                size="large"
                              />
                            ) : (
                              <FrameLayout
                                header={
                                  <Header
                                    userName={userName}
                                    onSettingsClick={goToSettings}
                                    onLogout={handleLogout}
                                  />
                                }
                                menu={
                                  <Menu history={history} location={location} />
                                }
                                content={<Content />}
                                footer={<FooterContainer />}
                                insight={<InsightContainer />}
                              />
                            )}
                          </AppContainer>
                          <ActionStatusInfo />
                          <GlobalDispatchWatcher />
                          <NotificationsContainer />
                          <ErrorContainer />
                        </>
                      )}
                    </AppearanceContext.Consumer>
                  </EditModeContextProvider>
                </ErrorBoundary>
              </InsightContext.Provider>
            </FooterContext.Provider>
          </AppearanceProvider>
        </PermissionsProvider>
      );
    },
  ),
);
